import {
  Button,
  Combobox,
  DialogActions,
  Listbox,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Subtitle1,
  Text,
  Toaster,
  useId,
} from "@fluentui/react-components";
import { Delete16Regular } from "@fluentui/react-icons";
import { useToast } from "@hooks/useToast";
import { useModal } from "@shared/modal/useModal";
import { sessions } from "@strings/index";
import { useAuth } from "@taskpane/AuthContext";
import { reportError } from "@utils/observability/reportError";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./SessionSelection.styles";
import { useSessions, type Session } from "@hooks/useSessions";

const sessionsStr = sessions();

interface SessionSelectionProps {
  setAddingSession: React.Dispatch<React.SetStateAction<string[] | null>>;
  /** Optional UI-coordination flag used by SessionLoadScreen to hide its
   * dialog while the delete-confirmation modal is open. */
  setDeletingSession?: React.Dispatch<React.SetStateAction<boolean>>;
  compact?: boolean;
}

export const SessionSelection: React.FC<SessionSelectionProps> = ({
  setAddingSession,
  setDeletingSession = null,
  compact = false,
}) => {
  const navigate = useNavigate();
  const { session, sessions, loadSession, deleteSession } = useSessions();
  const [hoveredRow, setHoveredRow] = useState("");
  const styles = useStyles();
  const { logout } = useAuth();

  const options = useMemo(
    () =>
      sessions.map((s: Session) => ({
        value: s.session_id,
        children: (
          <div className={styles.textFormatting}>
            <Text>{s.session_name}</Text>
            <Text italic>{s.session_mode}</Text>
          </div>
        ),
        text: s.session_name, // used for type-ahead matching
      })),
    [sessions, styles.textFormatting]
  );

  const [inputValue, setInputValue] = useState<string>("");
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [matchingOptions, setMatchingOptions] = useState([...options]);

  const { openModal, closeModal } = useModal();
  const toasterId = useId("toast");
  const { showToast } = useToast(toasterId);

  useEffect(() => {
    setMatchingOptions([...options]);
  }, [options]);

  useEffect(() => {
    if (session) {
      setInputValue(session.session_name);
      setSelectedSessionId(session.session_id);
    }
  }, [session]);

  const onSelect: (event: SelectionEvents, data: OptionOnSelectData) => void | undefined = (_event, data) => {
    const id = data.optionValue; // data.optionValue is session_id
    if (id === "Create Project") {
      setAddingSession([inputValue]);
      return;
    }
    id && setSelectedSessionId(id);
    const picked = sessions.find((s: Session) => s.session_id === id);
    setInputValue(picked?.session_name ?? "");
    if (id && compact) {
      loadSession(id);
      navigate("/chat");
    }
  };

  const handleLoadClick = () => {
    if (!selectedSessionId) return;
    loadSession(selectedSessionId);
    navigate("/chat");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setInputValue(value);

    if (!value) {
      setMatchingOptions([...options]);
    } else {
      const matches = options.filter((option: { text: string }) =>
        option.text.toLowerCase().includes(value.toLowerCase())
      );
      setMatchingOptions(matches);
    }
  };

  const handleDeleteSession = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, sessionId: string, sessionName: string) => {
      event.preventDefault();
      event.stopPropagation();
      if (setDeletingSession) {
        setDeletingSession(true);
      }
      openModal({
        title: "Confirm Session Deletion",
        content: `Are you sure you want to delete session: ${sessionName}`,
        confirmText: "Delete",
        cancelText: "Cancel",
        onConfirm: async () => {
          const wasCurrent = session?.session_id === sessionId;
          try {
            await deleteSession.mutateAsync(sessionId);
            if (wasCurrent) navigate("/");
          } catch (e) {
            showToast({
              title: "Error",
              message: "An unexpected error occurred while attempting to delete the session.",
              intent: "error",
            });
            reportError("session.delete", e, { sessionId });
          } finally {
            closeModal();
            if (setDeletingSession) {
              setDeletingSession(false);
            }
          }
        },
        onCancel: () => {
          if (setDeletingSession) setDeletingSession(false);
          closeModal();
        },
      });
    },
    [deleteSession, openModal, closeModal, showToast, setDeletingSession, session, navigate]
  );

  const isLoadDisabled = !selectedSessionId;

  return (
    <div className={styles.sessionManagementSurface}>
      <Toaster className={styles.toastLevel} toasterId={toasterId} position="top" />
      {!compact && <Subtitle1 className={styles.title}>{sessionsStr.sessionManagement}</Subtitle1>}

      <div className={styles.selectionOptions}>
        <Combobox
          className={compact ? styles.dropDownButtonNav : styles.dropDownButton}
          freeform
          placeholder={sessionsStr.projectSelectionPlaceholder}
          value={inputValue}
          selectedOptions={selectedSessionId ? [selectedSessionId] : []}
          onChange={handleChange}
          onOptionSelect={(event, data) => onSelect(event, data as any)}
        >
          <Option key={"createProject"} value="Create Project" text={"Create Project"}>
            <Text>Create Project</Text>
          </Option>
          <Listbox className={styles.listboxSize}>
            {matchingOptions.map((option) => (
              <Option
                key={option.value}
                value={option.value}
                text={option.text}
                onMouseEnter={() => setHoveredRow(option.value)}
                onMouseLeave={() => setHoveredRow("")}
              >
                <div className={styles.optionsChildrenSpacing}>
                  {option.children}
                  {hoveredRow === option.value && (
                    <Button
                      className={styles.sessionButtons}
                      icon={<Delete16Regular />}
                      size="small"
                      appearance="subtle"
                      onClick={(event) => handleDeleteSession(event, option.value, option.text)}
                    />
                  )}
                </div>
              </Option>
            ))}
          </Listbox>
        </Combobox>
      </div>

      {!compact && (
        <DialogActions className={styles.actions}>
          <Button className={styles.loadSessionButton} onClick={logout} appearance="secondary">
            {sessionsStr.cancelSession}
          </Button>
          <Button
            className={styles.loadSessionButton}
            onClick={handleLoadClick}
            disabled={isLoadDisabled}
            appearance="primary"
          >
            {sessionsStr.loadSession}
          </Button>
        </DialogActions>
      )}
    </div>
  );
};
